/**
 * Interactive Artifact Bridge Protocol (SCH-3664, U1, KTD7)
 *
 * The share viewer hands the sandboxed page frame a private `MessageChannel` port and relays only the messages
 * below. This union is the bridge's allowlist: the viewer parses every message from the frame with
 * `ArtifactBridgeMessageSchema` (or the narrower `ArtifactBridgeFrameMessageSchema`) and drops anything else.
 *
 * Flow (plan, High-Level Technical Design):
 * - viewer → frame window: `init`, once per frame generation, with the port in the transfer list (target `*`,
 *   since the frame's origin is opaque). The port itself never appears in the data.
 * - frame → viewer, on the port: `hello` (protocol 1).
 * - viewer → frame: `granted` (canEdit and the allowed methods), then `snapshot` on every data change and `state`
 *   on every lifecycle change (live, stale, cut) and when the data subscription is lost (disconnected).
 * - frame → viewer: `request { id, method, params }`; the viewer answers each with exactly one `response`.
 * - frame → viewer: `open-link`, the link interception message (the same as the `open-link` request method).
 *
 * Every branch is `.strict()`, and none carries an artifact id, data handle, credential or store URL: the viewer
 * holds those itself. Methods are an explicit union with their own params, so an unknown method or a params bag
 * that does not match its method fails the parse.
 *
 * The 64 KB byte cap applies to every message in both directions except `snapshot`, and the schemas enforce it on
 * the serialized message. A snapshot carries the artifact's whole data, which the store's item and value limits
 * can take past 64 KB; it is bounded by those limits instead, so every page within them stays live (R7). The cap
 * keeps guarding everything the untrusted page sends. The in-flight and rate limits are exported for the viewer to
 * enforce; a schema cannot enforce them.
 *
 * Plan: docs/plans/2026-10-19-1623-feat-ai-artifacts-live-data-plan.md (U1).
 */

import { z } from 'zod'
import {
  ArtifactDataAddParamsSchema,
  ArtifactDataItemSchema,
  ArtifactDataMoveParamsSchema,
  ArtifactDataOutsiderItemSchema,
  ArtifactDataRemoveParamsSchema,
  ArtifactDataSetParamsSchema,
  ArtifactDataWriteResultSchema,
  ARTIFACT_DATA_MAX_ITEMS
} from './artifact-data'

// =============================================================================
// Limits (KTD7), enforced by the viewer
// =============================================================================

export const ARTIFACT_BRIDGE_PROTOCOL_VERSION = 1
/**
 * No message on the bridge, in either direction, may be larger than this once serialized as JSON (KTD7). Every
 * message schema below refuses a larger one, except the viewer's `snapshot`, which the store's data limits bound.
 */
export const ARTIFACT_BRIDGE_MAX_MESSAGE_BYTES = 64 * 1024
/** Requests beyond this many unanswered are refused `busy`. */
export const ARTIFACT_BRIDGE_MAX_IN_FLIGHT = 20
/** Write requests per second, sustained, and the burst allowed above it (a token bucket). */
export const ARTIFACT_BRIDGE_WRITE_RATE_PER_SECOND = 10
export const ARTIFACT_BRIDGE_WRITE_BURST = 30
export const ARTIFACT_BRIDGE_URL_MAX_LENGTH = 2_048

// =============================================================================
// Primitives
// =============================================================================

/** Methods that change data; only editors are granted them. */
export const ARTIFACT_BRIDGE_WRITE_METHODS = ['set', 'add', 'remove', 'move'] as const
export const ArtifactBridgeMethodSchema = z.enum([...ARTIFACT_BRIDGE_WRITE_METHODS, 'open-link'])
export type ArtifactBridgeMethod = z.infer<typeof ArtifactBridgeMethodSchema>

/** A request's correlation id, chosen by the page and echoed on its response. */
export const ArtifactBridgeRequestIdSchema = z.string().min(1).max(64)

/** The message's size, serialized as UTF-8 JSON, in bytes. */
export function artifactBridgeMessageBytes(message: unknown): number {
  return new TextEncoder().encode(JSON.stringify(message)).length
}

/** Refuses a message over `ARTIFACT_BRIDGE_MAX_MESSAGE_BYTES`; a viewer `snapshot` is exempt (see the header). */
const withinMessageCap = <T>(message: T, ctx: z.RefinementCtx<T>) => {
  const type = typeof message === 'object' && message !== null ? (message as { type?: unknown }).type : undefined
  if (type === 'snapshot') return
  if (artifactBridgeMessageBytes(message) > ARTIFACT_BRIDGE_MAX_MESSAGE_BYTES) {
    ctx.addIssue({ code: 'custom', message: `larger than ${ARTIFACT_BRIDGE_MAX_MESSAGE_BYTES} bytes` })
  }
}

const ProtocolSchema = z.literal(ARTIFACT_BRIDGE_PROTOCOL_VERSION)
const VersionSchema = z.number().int().positive()

/**
 * A link the page asks the viewer to open (through the interstitial, KTD14). Absolute `http:` or `https:` only:
 * `javascript:`, `data:`, `file:` and relative URLs fail.
 */
export const ArtifactBridgeLinkUrlSchema = z
  .string()
  .min(1)
  .max(ARTIFACT_BRIDGE_URL_MAX_LENGTH)
  .refine(value => {
    try {
      const { protocol } = new URL(value)
      return protocol === 'https:' || protocol === 'http:'
    } catch {
      return false
    }
  }, 'an absolute http(s) URL')

export const ArtifactBridgeOpenLinkParamsSchema = z.object({ url: ArtifactBridgeLinkUrlSchema }).strict()
export type ArtifactBridgeOpenLinkParams = z.infer<typeof ArtifactBridgeOpenLinkParamsSchema>

/** Why the viewer refused a request. */
export const ArtifactBridgeErrorSchema = z.enum([
  'invalid_request',
  'not_granted',
  'undeclared_key',
  'limit_exceeded',
  'not_found',
  'rate_limited',
  'busy',
  'unavailable'
])
export type ArtifactBridgeError = z.infer<typeof ArtifactBridgeErrorSchema>

/**
 * The bridge state machine's states the page is told about (R23), plus `disconnected`: the viewer lost its data
 * subscription, so the page disables editing and shows "reconnecting" until the next snapshot (KTD6 Feedback).
 */
export const ArtifactBridgeStateSchema = z.enum(['live', 'stale', 'cut', 'disconnected'])
export type ArtifactBridgeState = z.infer<typeof ArtifactBridgeStateSchema>

// =============================================================================
// Messages
// =============================================================================

/** viewer → frame window: the port rides in the transfer list, never in the data. */
export const ArtifactBridgeInitMessageSchema = z.object({ type: z.literal('init'), protocol: ProtocolSchema }).strict()

/** frame → viewer, the first message on the port. */
export const ArtifactBridgeHelloMessageSchema = z
  .object({ type: z.literal('hello'), protocol: ProtocolSchema })
  .strict()

/** viewer → frame: what this viewer may do. A viewer who cannot edit is granted no write method. */
export const ArtifactBridgeGrantedMessageSchema = z
  .object({
    type: z.literal('granted'),
    canEdit: z.boolean(),
    methods: z.array(ArtifactBridgeMethodSchema).max(ArtifactBridgeMethodSchema.options.length)
  })
  .strict()
  .superRefine((message, ctx) => {
    if (new Set(message.methods).size !== message.methods.length) {
      ctx.addIssue({ code: 'custom', path: ['methods'], message: 'methods repeat' })
    }
    const writes: readonly string[] = ARTIFACT_BRIDGE_WRITE_METHODS
    if (!message.canEdit && message.methods.some(method => writes.includes(method))) {
      ctx.addIssue({ code: 'custom', path: ['methods'], message: 'a viewer who cannot edit gets no write method' })
    }
  })

const request = <M extends ArtifactBridgeMethod, P extends z.ZodType>(method: M, params: P) =>
  z
    .object({ type: z.literal('request'), id: ArtifactBridgeRequestIdSchema, method: z.literal(method), params })
    .strict()

/** frame → viewer: one call, answered by exactly one response with the same id. */
export const ArtifactBridgeRequestMessageSchema = z.discriminatedUnion('method', [
  request('set', ArtifactDataSetParamsSchema),
  request('add', ArtifactDataAddParamsSchema),
  request('remove', ArtifactDataRemoveParamsSchema),
  request('move', ArtifactDataMoveParamsSchema),
  request('open-link', ArtifactBridgeOpenLinkParamsSchema)
])
export type ArtifactBridgeRequestMessage = z.infer<typeof ArtifactBridgeRequestMessageSchema>

/** viewer → frame: the ack or refusal for one request. A write's result names the item it touched. */
export const ArtifactBridgeResponseMessageSchema = z.discriminatedUnion('ok', [
  z
    .object({
      type: z.literal('response'),
      id: ArtifactBridgeRequestIdSchema,
      ok: z.literal(true),
      result: ArtifactDataWriteResultSchema.nullable()
    })
    .strict(),
  z
    .object({
      type: z.literal('response'),
      id: ArtifactBridgeRequestIdSchema,
      ok: z.literal(false),
      error: ArtifactBridgeErrorSchema
    })
    .strict()
])
export type ArtifactBridgeResponseMessage = z.infer<typeof ArtifactBridgeResponseMessageSchema>

/**
 * viewer → frame: the current shown items and the version the store reports. Team viewers get names; everyone
 * else gets the outsider items, which cannot carry one (R9). Unlike every other message, a snapshot is not held to
 * `ARTIFACT_BRIDGE_MAX_MESSAGE_BYTES`: it carries all the artifact's data, bounded by the item cap and each item's
 * value limits, so a page whose data is within the store's limits never loses live updates (R7).
 */
export const ArtifactBridgeSnapshotMessageSchema = z
  .object({
    type: z.literal('snapshot'),
    version: VersionSchema,
    items: z.union([
      z.array(ArtifactDataItemSchema).max(ARTIFACT_DATA_MAX_ITEMS),
      z.array(ArtifactDataOutsiderItemSchema).max(ARTIFACT_DATA_MAX_ITEMS)
    ])
  })
  .strict()

/** viewer → frame: a lifecycle change (R23). */
export const ArtifactBridgeStateMessageSchema = z
  .object({ type: z.literal('state'), state: ArtifactBridgeStateSchema })
  .strict()

/** frame → viewer: a link click to open, without waiting for an answer. */
export const ArtifactBridgeOpenLinkMessageSchema = z
  .object({ type: z.literal('open-link'), url: ArtifactBridgeLinkUrlSchema })
  .strict()

/** Every message either side may send, each at most `ARTIFACT_BRIDGE_MAX_MESSAGE_BYTES` except a `snapshot`. */
export const ArtifactBridgeMessageSchema = z
  .discriminatedUnion('type', [
    ArtifactBridgeInitMessageSchema,
    ArtifactBridgeHelloMessageSchema,
    ArtifactBridgeGrantedMessageSchema,
    ArtifactBridgeRequestMessageSchema,
    ArtifactBridgeResponseMessageSchema,
    ArtifactBridgeSnapshotMessageSchema,
    ArtifactBridgeStateMessageSchema,
    ArtifactBridgeOpenLinkMessageSchema
  ])
  .superRefine(withinMessageCap)
export type ArtifactBridgeMessage = z.infer<typeof ArtifactBridgeMessageSchema>

/** The messages the viewer accepts from the frame's port: hello, requests and link clicks, each within the cap. */
export const ArtifactBridgeFrameMessageSchema = z
  .discriminatedUnion('type', [
    ArtifactBridgeHelloMessageSchema,
    ArtifactBridgeRequestMessageSchema,
    ArtifactBridgeOpenLinkMessageSchema
  ])
  .superRefine(withinMessageCap)
export type ArtifactBridgeFrameMessage = z.infer<typeof ArtifactBridgeFrameMessageSchema>
